// filepath: apps/web/src/features/promote/AssertionsList.tsx
//
// FR-UI-PROM-2 AC-2 — renders the list of carried assertions from
// the NEEDS_REVIEW / verified envelope (artifact.attempts[].assertion_results).
//
// INV-9 compliant: every server-supplied string is rendered as a
// text node. The `expected` / `actual` / `fields` values are JSON-
// stringified into <pre> blocks; the `terminal_failure_reason` is
// rendered as a text span.
//
// Empty state: when `artifact` is null (human-authored submission)
// the component renders the chrome-only empty-state copy. This
// satisfies AC-2's "passed assertions are listed" — a human
// submission has no assertions to list, and the reviewer must see
// the empty-state copy explicitly (no implicit "skip" path).

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type {
  ArtifactEnvelope,
  AssertionResult,
  ArtifactAttempt,
} from "./types";

interface AssertionsListProps {
  artifact: ArtifactEnvelope | null;
}

export function AssertionsList({
  artifact,
}: AssertionsListProps): React.ReactElement {
  const { t } = useTranslation();

  if (!artifact) {
    return (
      <p
        className="text-sm text-muted-foreground"
        aria-live="polite"
        data-testid="assertions-list-empty"
      >
        {t("promote.gate.assertions.none")}
      </p>
    );
  }

  const attempts = artifact.attempts ?? [];
  // One row per (attempt, acceptance-criterion) tuple. When an
  // attempt has zero assertion_results (e.g. compile_error before
  // assertions ran), the row still appears so the reviewer sees
  // the gap.
  const rows = attempts.flatMap((a: ArtifactAttempt) => {
    const results = a.assertion_results ?? [];
    if (results.length === 0) {
      return [
        {
          attempt: a,
          result: null as AssertionResult | null,
          key: `${a.attempt_number}:empty`,
        },
      ];
    }
    return results.map((r, idx) => ({
      attempt: a,
      result: r,
      key: `${a.attempt_number}:${idx}`,
    }));
  });

  if (rows.length === 0) {
    return (
      <p
        className="text-sm text-muted-foreground"
        aria-live="polite"
        data-testid="assertions-list-empty"
      >
        {t("promote.gate.assertions.none")}
      </p>
    );
  }

  return (
    <ul
      aria-label={t("promote.gate.section.assertions")}
      className="space-y-3"
      data-testid="assertions-list"
    >
      {rows.map(({ attempt, result, key }) => (
        <li
          key={key}
          aria-label={`attempt ${attempt.attempt_number} ${result ? "assertion" : "no-assertions"}`}
          className="rounded border border-border p-3"
          data-testid="assertions-list-row"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-muted-foreground">
              attempt {attempt.attempt_number}
            </span>
            <AssertionBadge result={result} />
          </div>

          {result ? (
            <div className="space-y-1 text-xs">
              {result.expected !== undefined && (
                <AssertionField label="expected" value={result.expected} />
              )}
              {result.actual !== undefined && (
                <AssertionField label="actual" value={result.actual} />
              )}
              {result.fields && Object.keys(result.fields).length > 0 && (
                <div>
                  <span className="text-muted-foreground">fields:</span>
                  <pre className="font-mono whitespace-pre-wrap break-all">
                    {stringifySafe(result.fields)}
                  </pre>
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              terminal_failure_reason:
              <span className="ml-1 font-mono">
                {attempt.terminal_failure_reason || "(none)"}
              </span>
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

function AssertionBadge({
  result,
}: {
  result: AssertionResult | null;
}): React.ReactElement {
  const { t } = useTranslation();
  // INV-9: no client-side evaluation. The verdict is reported by
  // the agent run (server-side) — when present, we render the
  // badge as text only. We have no authoritative "pass/fail" bit
  // in the wire (FR-AGT-3 shape: {expected, actual, fields}); a
  // future FR may add a `verdict` field. For v1 we surface a
  // neutral "recorded" badge so the reviewer sees the row is
  // populated without falsely claiming a pass.
  const label = result ? t("promote.gate.assertions.recorded") : "—";
  return (
    <span
      className="rounded bg-muted px-2 py-0.5 text-xs"
      data-testid={
        result ? "assertion-badge-recorded" : "assertion-badge-empty"
      }
    >
      {label}
    </span>
  );
}

function AssertionField({
  label,
  value,
}: {
  label: string;
  value: unknown;
}): React.ReactElement {
  return (
    <div>
      <span className="text-muted-foreground">{label}:</span>{" "}
      <pre className="inline font-mono whitespace-pre-wrap break-all">
        {stringifySafe(value)}
      </pre>
    </div>
  );
}

function stringifySafe(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    // Circular references / BigInt / etc. — fall back to a
    // deterministic text representation. Never use eval / toString
    // with a getter (INV-9 / client-side evaluation ban).
    return "[unserializable]";
  }
}
